import { TFunction } from 'i18next';
import { IntegrationConfigBase } from 'models/integrations/integrationConfigBase';
import { IIntegrationEvidence } from 'models/integrations/integrationEvidence';
import { toLocaleDateTime } from 'utils/datetime';

export const SslLabsAllowedGrades = ['A+', 'A', 'A-', 'B', 'C', 'D', 'E', 'F', 'T', 'M'] as const;
export type SslLabsGrade = (typeof SslLabsAllowedGrades)[number];

export const SslLabsResultAggregations = ['worst-score', 'average-score'] as const;
export type SslLabsResultAggregation = (typeof SslLabsResultAggregations)[number];

export class WebsiteTarget {
  host: string = '';

  displayName: string = '';
}

export class IntegrationConfig_SslLabs extends IntegrationConfigBase {
  targets: WebsiteTarget[] = [];

  minimumAcceptedGrade: SslLabsGrade = 'A';
}

export class SslLabsAuthData {
  firstName: string = '';

  lastName: string = '';

  email: string = '';

  organization: string = '';
}

export class SslLabsEndpointEvidence {
  ipAddress: string = '';

  serverName: string = '';

  grade: string = '';

  gradeTrustIgnored: string = '';

  futureGrade: string = '';

  hasWarnings: boolean = false;

  isExceptional: boolean = false;

  certificateNotAfterUtc?: Date;

  certificateIssuer: string = '';

  certificateSubject: string = '';

  protocols: string[] = [];
}

export class SslLabsSiteEvidence {
  host: string = '';

  displayName: string = '';

  assessmentStatus: string = '';

  testTimeUtc?: Date;

  worstGrade: string = '';

  score: number = 0;

  passed: boolean = false;

  endpoints: SslLabsEndpointEvidence[] = [];
}

export class SslLabsEvidence implements IIntegrationEvidence {
  isCapped: boolean = false;

  evaluatedAtUtc: Date = new Date();

  minimumAcceptedGrade: string = 'A';

  resultAggregation: SslLabsResultAggregation = 'worst-score';

  resultScore: number = 0;

  passed: boolean = false;

  sites: SslLabsSiteEvidence[] = [];

  private getResultAggregationLabel(t: TFunction<['translation', 'integrations']>): string {
    switch (this.resultAggregation) {
      case 'average-score':
        return t('integrations:SslLabs.Report.ResultAggregationAverageScore');
      case 'worst-score':
      default:
        return t('integrations:SslLabs.Report.ResultAggregationWorstScore');
    }
  }

  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const sb: string[] = [];

    sb.push(`## ${t('integrations:SslLabs.Report.SummaryHeading', { date: toLocaleDateTime(this.evaluatedAtUtc) })}`);
    sb.push(`- ${t('integrations:SslLabs.Report.ResultScore', { score: this.resultScore })}`);
    sb.push(`- ${t('integrations:SslLabs.Report.MinimumAcceptedGrade', { grade: this.minimumAcceptedGrade })}`);
    sb.push(`- ${t('integrations:SslLabs.Report.ResultAggregation', { value: this.getResultAggregationLabel(t) })}`);
    sb.push(`- ${t('integrations:SslLabs.Report.TotalSites', { count: this.sites.length })}`);
    sb.push(`## ${t('integrations:SslLabs.Report.SitesHeading', { count: this.sites.length })}`);

    this.sites.forEach((site) => {
      sb.push(
        `- ${t('integrations:SslLabs.Report.SiteLine', {
          name: site.displayName || site.host,
          host: site.host,
          grade: site.worstGrade,
          score: site.score,
          passed: site.passed ? t('translation:General.Words.Yes') : t('translation:General.Words.No'),
        })}`,
      );
    });

    return sb.join('\n');
  }
}
